// Shared client-side types and presentation metadata for Creative Strategist
// (Task #2375). Kept in one place so the home screen, project list and project
// detail page agree on statuses and labels.

// The per-brand flag. Off (the default) = the feature does not exist for that
// brand, for everyone including masters.
export const CREATIVE_STRATEGIST_BRAND_FLAG = "enableCreativeStrategist";

// Gated exactly like every other Creative Strategist API: a 403 means the flag
// is off or the global switch is in Hard Stop.
export function csStatusKey(brandId: string | undefined): string {
  return `/api/creative-strategist/brands/${brandId}/status`;
}

export interface CreativeStrategistStatus {
  enabled: boolean;
  mode: string;
}

export interface CreativeStrategistProjectStats {
  tasksTotal: number;
  tasksComplete: number;
  tasksBlocked: number;
  tasksAwaitingUser: number;
  tasksFailed: number;
  deliverablesTotal: number;
  deliverablesApproved: number;
  deliverablesAwaitingUser: number;
  decisionsAwaitingUser: number;
  /** Rejected decisions — each one parks the tasks that inherited it. */
  decisionsRejected: number;
  blockersOpen: number;
  /**
   * Tasks the system gave up on and handed back — out of automatic revisions,
   * or escalated by review. Distinct from `tasksAwaitingUser`, which counts a
   * status; this counts the flag, so it survives whatever status the task
   * ended in.
   */
  tasksNeedingAttention: number;
  /** Running, in review, or being revised — work that is still moving. */
  tasksInFlight: number;
}

export interface CreativeStrategistProject {
  id: string;
  workspaceId: string;
  brandId: string;
  createdBy: string | null;
  title: string;
  objective: string | null;
  description: string | null;
  status: string;
  currentPhase: string | null;
  progressPercentage: number;
  chatThreadId: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  completedAt: string | null;
  stats: CreativeStrategistProjectStats;
  brandName?: string | null;
}

export interface CreativeStrategistActivityEvent {
  id: string;
  projectId: string;
  brandId: string;
  taskId: string | null;
  eventType: string;
  actorType: string;
  actorUserId: string | null;
  summary: string;
  detailJson: unknown;
  createdAt: string | null;
}

export const CS_PROJECT_STATUS_LABELS: Record<string, string> = {
  planning: "Planning",
  awaiting_approval: "Awaiting approval",
  running: "Running",
  blocked: "Blocked",
  complete: "Complete",
  canceled: "Canceled",
};

export const CS_PROJECT_STATUS_CLASSES: Record<string, string> = {
  planning: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300",
  awaiting_approval: "bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300",
  running: "bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300",
  blocked: "bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300",
  complete: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300",
  canceled: "bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400",
};

export const CS_TERMINAL_STATUSES = new Set(["complete", "canceled"]);

// Spec §8 starter prompts.
export const CS_STARTER_PROMPTS = [
  "Build a paid acquisition campaign",
  "Build landing pages around these pain points",
  "Develop a new offer",
  "Create creative around an existing marketing angle",
  "Turn this document into a campaign",
  "Build a launch plan",
  "Make ads from this strategy",
];

// Spec §49 project tabs, in order. `?tab=` values double as the path-style
// segment the spec's own URLs use.
export const CS_PROJECT_TABS = [
  "overview",
  "plan",
  "tasks",
  "deliverables",
  "decisions",
  "files",
  "results",
  "activity",
] as const;
export type CreativeStrategistProjectTab = (typeof CS_PROJECT_TABS)[number];

export const CS_PROJECT_TAB_LABELS: Record<CreativeStrategistProjectTab, string> = {
  overview: "Overview",
  plan: "Plan",
  tasks: "Tasks",
  deliverables: "Deliverables",
  decisions: "Decisions",
  files: "Files",
  results: "Results",
  activity: "Activity",
};

export interface CreativeStrategistTemplateEntry {
  /** Registry row id; null for a discovered candidate nobody has reviewed. */
  id: string | null;
  assetType: string;
  system: string;
  templateType: string | null;
  templateId: string | null;
  name: string;
  description: string | null;
  previewUrl: string | null;
  sourceHref: string | null;
  status: string;
  isDefault: boolean;
  approvedFor: string[];
  avoidFor: string[];
  lockedRules: string[];
  flexibleRules: string[];
  notes: string | null;
  performanceScore: number | null;
  source: string | null;
  approvedBy: string | null;
  approvedAt: string | null;
  updatedAt: string | null;
  permitted: boolean;
  usageCount: number;
}

// ── Chat + context assets (Task #2377) ────────────────────────────────────

export type CreativeStrategistMessageRole = "user" | "strategist" | "system";
export type CreativeStrategistMessageStatus = "pending" | "sent" | "failed";

export interface CreativeStrategistContextAsset {
  id: string;
  projectId: string;
  messageId: string | null;
  fileName: string;
  fileType: string;
  fileSize: number | null;
  storageUrl: string;
  purpose: string;
  userDescription: string | null;
  extractionStatus: string;
  analysisStatus: string;
  hasExtractedText: boolean;
  analysisSummary: string | null;
  analysis: unknown;
  processingError: string | null;
  uploadedBy: string | null;
  createdAt: string | null;
}

export interface CreativeStrategistViewingContext {
  tab?: string | null;
  taskId?: string | null;
  deliverableId?: string | null;
}

export interface CreativeStrategistResolvedTarget {
  kind: "task" | "deliverable" | "tab" | "project";
  id: string | null;
  label: string;
}

export interface CreativeStrategistMessage {
  id: string;
  projectId: string;
  authorRole: CreativeStrategistMessageRole;
  authorUserId: string | null;
  body: string;
  status: CreativeStrategistMessageStatus;
  errorMessage: string | null;
  replyToMessageId: string | null;
  viewingContext: CreativeStrategistViewingContext | null;
  intent: unknown;
  createdAt: string | null;
  attachments: CreativeStrategistContextAsset[];
}

// Mirrors server/services/creative-strategist/uploads.ts — the picker must
// offer exactly what the server accepts.
export const CS_UPLOAD_ACCEPT =
  ".png,.jpg,.jpeg,.webp,.pdf,.docx,.txt,.csv,.xlsx,image/png,image/jpeg,image/webp,application/pdf,text/plain,text/csv";
export const CS_ACCEPTED_LABEL = "PNG, JPG, WebP, PDF, DOCX, TXT, CSV or XLSX";
export const CS_MAX_FILE_BYTES = 25 * 1024 * 1024;
export const CS_MAX_FILES_PER_REQUEST = 10;

export const CS_ASSET_PURPOSE_LABELS: Record<string, string> = {
  reference_image: "Reference image",
  document: "Document",
  data: "Data",
  notes: "Notes",
  other: "Other",
};

export function csIsImageAsset(asset: { fileType: string }): boolean {
  return typeof asset.fileType === "string" && asset.fileType.startsWith("image/");
}

export function csFormatBytes(bytes: number | null | undefined): string {
  if (!bytes || bytes <= 0) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function csMessagesKey(projectId: string | undefined): string {
  return `/api/creative-strategist/projects/${projectId}/messages`;
}

export function csAssetsKey(projectId: string | undefined): string {
  return `/api/creative-strategist/projects/${projectId}/context-assets`;
}

// ── Plan, resolution and task graph (Task #2378) ──────────────────────────
//
// Mirrors server/services/creative-strategist/{catalog,requirements,planner}.ts.
// The five dependency states are the vocabulary the whole feature uses to
// explain WHY a piece of work is (or is not) in the plan.

export const CS_PLAN_GROUPS = [
  "strategy",
  "setup",
  "funnels",
  "creative",
  "content",
  "review",
  "delivery",
] as const;
export type CreativeStrategistPlanGroup = (typeof CS_PLAN_GROUPS)[number];

export const CS_PLAN_GROUP_LABELS: Record<CreativeStrategistPlanGroup, string> = {
  strategy: "Strategy",
  setup: "Setup",
  funnels: "Funnels",
  creative: "Creative",
  content: "Content",
  review: "Review",
  delivery: "Delivery",
};

export type CreativeStrategistDependencyStatus =
  | "EXISTING"
  | "USER_SUPPLIED"
  | "BACKFILL"
  | "GENERATE"
  | "BLOCKED";

export const CS_DEPENDENCY_STATUS_LABELS: Record<
  CreativeStrategistDependencyStatus,
  string
> = {
  EXISTING: "Already exists",
  USER_SUPPLIED: "You supplied it",
  BACKFILL: "Build from what you said",
  GENERATE: "Work it out",
  BLOCKED: "Blocked",
};

export const CS_DEPENDENCY_STATUS_CLASSES: Record<
  CreativeStrategistDependencyStatus,
  string
> = {
  EXISTING: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300",
  USER_SUPPLIED: "bg-sky-100 text-sky-700 dark:bg-sky-900 dark:text-sky-300",
  BACKFILL: "bg-indigo-100 text-indigo-700 dark:bg-indigo-900 dark:text-indigo-300",
  GENERATE: "bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300",
  BLOCKED: "bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300",
};

export interface CreativeStrategistPlanBlockerOption {
  key: string;
  label: string;
  description: string;
}

/**
 * A blocker the resolver computed, carried inside the plan/resolution JSON.
 * Distinct from `CreativeStrategistBlocker` further down, which is a persisted
 * blocker row the user can resolve — this one exists before any row does.
 */
export interface CreativeStrategistPlanBlocker {
  id: string;
  kind: "tool_unavailable" | "template_unavailable" | "missing_input";
  title: string;
  reason: string;
  toolKey?: string;
  requirementKey?: string;
  outputTypes: string[];
  options: CreativeStrategistPlanBlockerOption[];
}

export interface CreativeStrategistDependency {
  key: string;
  type: string;
  label: string;
  name: string;
  status: CreativeStrategistDependencyStatus;
  reason: string;
  source: "code" | "model" | "fallback";
  existingId: string | null;
  existingName: string | null;
  suppliedText: string | null;
  neededBy: string[];
  blockerId: string | null;
  question: string | null;
}

export interface CreativeStrategistResolvedOutput {
  outputType: string;
  label: string;
  pluralLabel: string;
  quantity: number;
  tool: string;
  toolLabel: string;
  group: CreativeStrategistPlanGroup;
  available: boolean;
  blockerIds: string[];
  templateAvailable: boolean;
  templateAvailability: string;
  requiredAccess: string;
  notes: string | null;
}

export interface CreativeStrategistPlanItem {
  key: string;
  group: CreativeStrategistPlanGroup;
  order: number;
  title: string;
  description: string;
  itemType: string;
  status: "planned" | "blocked";
  taskCount: number;
  taskType: string;
  executor: string;
  expectedOutputType: string | null;
  outputType: string | null;
  requirementKey: string | null;
  dependsOn: string[];
  dependencyState: string;
  templateAvailability: string | null;
  requiredAccess: string | null;
  blockerIds: string[];
  removed: boolean;
}

export interface CreativeStrategistPlanGroupSummary {
  group: CreativeStrategistPlanGroup;
  label: string;
  summary: string;
  itemKeys: string[];
}

export interface CreativeStrategistPlan {
  schemaVersion: number;
  generatedAt: string;
  planTitle: string;
  summary: string;
  items: CreativeStrategistPlanItem[];
  groups: CreativeStrategistPlanGroupSummary[];
  estimatedDeliverables: number;
  estimatedTasks: number;
  blockers: CreativeStrategistPlanBlocker[];
  discoveryRequired: boolean;
  source: "decision" | "deterministic";
}

export interface CreativeStrategistPlanState {
  resolutionStatus: "none" | "running" | "ready" | "blocked" | "failed";
  resolutionError: string | null;
  resolvedAt: string | null;
  planStatus: "none" | "proposed" | "approved";
  planVersion: number;
  planApprovedAt: string | null;
  plan: CreativeStrategistPlan | null;
  resolution: {
    outputs: CreativeStrategistResolvedOutput[];
    dependencies: CreativeStrategistDependency[];
    blockers: CreativeStrategistPlanBlocker[];
    discoveryRequired: boolean;
    readyToPlan: boolean;
    notes: string[];
    warnings: string[];
    source: "decision" | "deterministic";
  } | null;
}

export const CS_TASK_STATUS_LABELS: Record<string, string> = {
  not_started: "Not started",
  ready: "Ready",
  running: "Running",
  blocked: "Blocked",
  internal_review: "Internal review",
  revision: "Revising",
  user_review: "Needs you",
  complete: "Complete",
  failed: "Failed",
  canceled: "Canceled",
};

export const CS_TASK_STATUS_CLASSES: Record<string, string> = {
  not_started: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300",
  ready: "bg-sky-100 text-sky-700 dark:bg-sky-900 dark:text-sky-300",
  running: "bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300",
  blocked: "bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300",
  internal_review: "bg-violet-100 text-violet-700 dark:bg-violet-900 dark:text-violet-300",
  revision: "bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-300",
  user_review: "bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300",
  complete: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300",
  failed: "bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300",
  canceled: "bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400",
};

/** Column order for the board view. */
export const CS_TASK_BOARD_COLUMNS = [
  "not_started",
  "ready",
  "running",
  "user_review",
  "complete",
] as const;

export interface CreativeStrategistTask {
  id: string;
  projectId: string;
  parentTaskId: string | null;
  title: string;
  description: string | null;
  taskType: string | null;
  status: string;
  assignedExecutor: string | null;
  priority: number;
  inputsJson: Record<string, unknown> | null;
  expectedOutputType: string | null;
  acceptanceCriteriaJson: string[] | null;
  decisionRefsJson: unknown;
  contextAssetRefsJson: unknown;
  attemptCount: number;
  /** Automatic revisions spent so far — capped, so this is also the budget. */
  revisionCount: number;
  /**
   * Set when the system stopped trying and handed the work back: out of
   * revisions, or escalated by review. Non-null is the "needs you" flag.
   */
  attentionReason: string | null;
  lastError: string | null;
  outputRef: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  startedAt: string | null;
  completedAt: string | null;
  dependencyCount: number;
  unmetDependencyCount: number;
  dependencyState: string;
  planGroup: string | null;
  planItemKey: string | null;
  requiresUserAction: boolean;
  /** Decisions this task inherited that the user rejected — work is on hold. */
  blockedByDecisions: Array<{ id: string; decisionType: string; title: string | null }>;
}

/** One dispatch of a task through a tool adapter (Task #2381). */
export interface CreativeStrategistExecution {
  id: string;
  executionKey: string;
  attemptNumber: number;
  executor: string;
  jobType: string;
  status: string;
  underlyingSystem: string | null;
  underlyingRunId: string | null;
  underlyingRefs: Record<string, unknown> | null;
  lastError: string | null;
  cancelRequestedAt: string | null;
  createdAt: string | null;
  startedAt: string | null;
  completedAt: string | null;
}

export interface CreativeStrategistTaskOutput {
  deliverables: Array<{
    id: string;
    deliverableType: string;
    title: string;
    status: string;
    previewUrl: string | null;
    createdAt: string | null;
  }>;
  links: Array<{
    id: string;
    outputSystem: string;
    outputType: string;
    outputId: string;
    outputUrl: string | null;
  }>;
}

export interface CreativeStrategistTaskDetail extends CreativeStrategistTask {
  dependsOn: Array<{
    id: string;
    title: string;
    status: string;
    dependencyType: string;
  }>;
  blocking: Array<{ id: string; title: string }>;
  files: Array<{ id: string; fileName: string; purpose: string }>;
  activity: CreativeStrategistActivityEvent[];
  createdObjects: CreativeStrategistCreatedObject[];
  decisions: CreativeStrategistDecision[];
  executions: CreativeStrategistExecution[];
  inputsSent: Array<{ label: string; value: string }>;
  output: CreativeStrategistTaskOutput | null;
  /** The verdict on the latest attempt, once internal review has run. */
  review: {
    verdict: string;
    score: number | null;
    summary: string | null;
    issues: CreativeStrategistReviewIssue[];
    criteriaChecks: Array<{ criterion: string; met: boolean; note?: string }>;
    reviewedAt: string | null;
    versionNumber: number;
  } | null;
  /** Every attempt at this task's deliverable, oldest first. */
  versionHistory: CreativeStrategistDeliverableVersion[];
  /** Child tasks created to revise this one's output. */
  revisions: Array<{
    id: string;
    title: string;
    status: string;
    attempt: number | null;
    instructions: string[];
    createdAt: string | null;
  }>;
}

// ── decisions ───────────────────────────────────────────────────────────────

/** A real PersonaOS object Creative Strategist created, and where it came from. */
export interface CreativeStrategistCreatedObject {
  id: string;
  objectType: string;
  objectId: string;
  sourceType: string;
  taskId: string | null;
  decisionRunId: string | null;
  createdAt: string | null;
  /** Where the object lives in the rest of the app, when it has a page. */
  href: string | null;
}

export interface CreativeStrategistDecision {
  id: string;
  decisionType: string;
  title: string | null;
  value: Record<string, unknown>;
  rationale: string | null;
  sourceType: string;
  status: string;
  version: number;
  decidedBy: string | null;
  supersedesId: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  createdObject: CreativeStrategistCreatedObject | null;
  /** Earlier versions, newest first. Never overwritten. */
  history: CreativeStrategistDecision[];
  dependentTasks?: Array<{ id: string; title: string; status: string }>;
}

export const CS_DECISION_TYPE_LABELS: Record<string, string> = {
  audience: "Audience",
  persona: "Persona",
  product: "Product",
  offer: "Offer",
  angle: "Narrative angle",
  pillar: "Content pillar",
  funnel: "Funnel",
  asset_mix: "Asset mix",
  template: "Template",
  cta: "Call to action",
  pricing: "Pricing",
  intent: "Project intent",
};

/** Provenance badge — where the value came from. */
export const CS_DECISION_SOURCE_LABELS: Record<string, string> = {
  existing: "Existing",
  user: "From you",
  backfilled: "Backfilled",
  generated: "Generated",
};

export const CS_DECISION_SOURCE_CLASSES: Record<string, string> = {
  existing: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300",
  user: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300",
  backfilled: "bg-sky-100 text-sky-700 dark:bg-sky-900 dark:text-sky-300",
  generated: "bg-violet-100 text-violet-700 dark:bg-violet-900 dark:text-violet-300",
};

export const CS_DECISION_STATUS_LABELS: Record<string, string> = {
  proposed: "Proposed",
  approved: "Approved",
  rejected: "Rejected",
  superseded: "Superseded",
};

export const CS_DECISION_STATUS_CLASSES: Record<string, string> = {
  proposed: "bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300",
  approved: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300",
  rejected: "bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300",
  superseded: "bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400",
};

export function csDecisionsKey(projectId: string | undefined): string {
  return `/api/creative-strategist/projects/${projectId}/decisions`;
}

export const CS_EXECUTOR_LABELS: Record<string, string> = {
  ads_express: "Ads Express",
  lp_express: "LP Express",
  ugc_express: "UGC Express",
  video_express: "Video Express Max",
  email_express: "Email Express",
  slideshow_express: "Slideshow Express",
  meme_generator: "Meme Generator",
  narrative_engine: "Narrative Engine",
  personas: "Personas",
  offers: "Offers",
  internal: "Creative Strategist",
  user: "You",
};

export function csPlanKey(projectId: string | undefined): string {
  return `/api/creative-strategist/projects/${projectId}/plan`;
}

export function csTasksKey(projectId: string | undefined): string {
  return `/api/creative-strategist/projects/${projectId}/tasks`;
}

export function csTaskKey(taskId: string | undefined): string {
  return `/api/creative-strategist/tasks/${taskId}`;
}

/**
 * Task types the backfill worker knows how to run on its own — the setup work
 * that turns something the user described into a real PersonaOS object. Keep
 * in lockstep with TASK_TYPE_TO_OBJECT in
 * server/services/creative-strategist/backfills.ts.
 */
export const CS_BACKFILL_TASK_TYPES: Record<string, string> = {
  create_persona: "persona",
  create_offer: "offer",
  create_narrative_angle: "narrative angle",
};

export function csIsBackfillTask(
  task: Pick<CreativeStrategistTask, "taskType">,
): boolean {
  return !!task.taskType && task.taskType in CS_BACKFILL_TASK_TYPES;
}

/**
 * Why a backfill task cannot be run right now, or null when it can — mirrors
 * what the server would answer, so the Tasks tab never offers a button that is
 * guaranteed to come back 409. The claim itself is still authoritative.
 */
export function csRunTaskBlockedReason(
  task: Pick<
    CreativeStrategistTask,
    "taskType" | "status" | "unmetDependencyCount" | "blockedByDecisions"
  >,
): string | null {
  if (!csIsBackfillTask(task)) return "This task is not a setup backfill.";
  if ((task.blockedByDecisions ?? []).length > 0) {
    return "On hold — replace the rejected decision on the Decisions tab first.";
  }
  if (task.status === "running") return "Already running.";
  if (task.status === "complete") return "Already done.";
  if (task.status === "canceled") return "This task was canceled.";
  if (task.unmetDependencyCount > 0) return "Waiting on an earlier task.";
  // `ready` and `failed` (a retry) are the two states the server's claim takes.
  if (task.status !== "ready" && task.status !== "failed") {
    return "Not ready to run yet.";
  }
  return null;
}

export function csCanRunTask(
  task: Pick<
    CreativeStrategistTask,
    "taskType" | "status" | "unmetDependencyCount" | "blockedByDecisions"
  >,
): boolean {
  return csRunTaskBlockedReason(task) === null;
}

/**
 * Executors Creative Strategist can actually drive today (Task #2381). Keep in
 * lockstep with the adapter registry in
 * server/services/creative-strategist/adapters/index.ts — a task assigned to
 * anything else is planned but not runnable yet, and must not offer Run.
 */
export const CS_EXECUTABLE_EXECUTORS = ["ads_express", "lp_express"] as const;

export function csIsToolTask(
  task: Pick<CreativeStrategistTask, "assignedExecutor">,
): boolean {
  return (CS_EXECUTABLE_EXECUTORS as readonly string[]).includes(
    task.assignedExecutor ?? "",
  );
}

/**
 * Mirror of the executor's own admission rule for a tool task. The executor
 * picks ready tasks up on its own, so this button is a "run it now" / retry —
 * it must never be offered for work the server would refuse.
 */
export function csToolRunBlockedReason(
  task: Pick<
    CreativeStrategistTask,
    "assignedExecutor" | "status" | "unmetDependencyCount" | "blockedByDecisions"
  >,
): string | null {
  if (!csIsToolTask(task)) return "No tool is wired up for this task yet.";
  if ((task.blockedByDecisions ?? []).length > 0) {
    return "On hold — replace the rejected decision on the Decisions tab first.";
  }
  if (task.status === "running") return "Already running.";
  if (task.status === "complete") return "Already done.";
  if (task.status === "canceled") return "This task was canceled.";
  if (task.unmetDependencyCount > 0) return "Waiting on an earlier task.";
  if (task.status !== "ready" && task.status !== "failed" && task.status !== "not_started") {
    return "Not ready to run yet.";
  }
  return null;
}

export function csCanRunToolTask(
  task: Pick<
    CreativeStrategistTask,
    "assignedExecutor" | "status" | "unmetDependencyCount" | "blockedByDecisions"
  >,
): boolean {
  return csToolRunBlockedReason(task) === null;
}

/** Only work that is actually in flight can be cancelled. */
export function csCanCancelTask(
  task: Pick<CreativeStrategistTask, "status">,
): boolean {
  return task.status === "running";
}

/**
 * Conditional polling (repo convention): poll only while something is moving,
 * so an idle board costs nothing.
 */
export function csHasActiveWork(
  tasks: Array<Pick<CreativeStrategistTask, "status">> | undefined,
): boolean {
  return (tasks ?? []).some((t) => t.status === "running" || t.status === "ready");
}

export const CS_TASKS_POLL_MS = 5000;

/**
 * Task statuses that cannot move again without a user action (a retry, a
 * decision change) — and every user action already invalidates the cache, so
 * polling on them buys nothing.
 */
export const CS_TASK_SETTLED_STATUSES = new Set(["complete", "canceled", "failed"]);

/** Whether a single watched task can still change on its own (executor sweep,
 * review, revision, an upstream task completing) — the detail poller's rule. */
export function csTaskStillMoving(
  task: Pick<CreativeStrategistTask, "status">,
): boolean {
  return !CS_TASK_SETTLED_STATUSES.has(task.status);
}

/**
 * Poll interval for the Tasks tab list (Task #2422). The old rule — poll only
 * while csHasActiveWork — had a wedge: a tab opened BEFORE the plan was
 * approved cached `[]`, active-work was false, and polling never started, so
 * the tasks the approval created could never appear without a reload. Same
 * shape for a fresh plan whose tasks are all `not_started`.
 *
 * So: poll while work is in flight, while the first load is still pending,
 * while the list is empty but the project could still produce tasks (any
 * non-terminal status), or while a driving project (planning/running) still
 * has tasks that can move on their own. A settled list on an idle or complete
 * project stops polling — the "idle plan costs nothing" convention holds.
 */
export function csTasksPollInterval(
  tasks: Array<Pick<CreativeStrategistTask, "status">> | undefined,
  projectStatus: string | null | undefined,
): number | false {
  if (tasks === undefined) return CS_TASKS_POLL_MS;
  if (csHasActiveWork(tasks)) return CS_TASKS_POLL_MS;
  const projectCanStillProduce =
    !!projectStatus && !CS_TERMINAL_STATUSES.has(projectStatus);
  if (tasks.length === 0) {
    return projectCanStillProduce ? CS_TASKS_POLL_MS : false;
  }
  const driving = projectStatus === "planning" || projectStatus === "running";
  if (driving && tasks.some((t) => csTaskStillMoving(t))) return CS_TASKS_POLL_MS;
  return false;
}

/**
 * Everything a strategist chat reply can now change (Task #2422): the reply
 * itself, uploaded assets, and — since chat drives the project — decisions,
 * the plan, the tasks it creates on approval, the project status, and the
 * activity feed. The chat panel invalidates all of these when a reply lands.
 */
export function csChatReplyInvalidationKeys(projectId: string): string[] {
  return [
    csMessagesKey(projectId),
    csAssetsKey(projectId),
    csTasksKey(projectId),
    csPlanKey(projectId),
    csDecisionsKey(projectId),
    `/api/creative-strategist/projects/${projectId}`,
    `/api/creative-strategist/projects/${projectId}/activity`,
  ];
}

/**
 * A decision can be replaced with the user's own answer while it is still
 * live. A superseded version is history — replacing it again would fork the
 * chain, so the replacement is where the next edit goes.
 */
export function csCanReplaceDecision(
  decision: Pick<CreativeStrategistDecision, "status">,
): boolean {
  return (
    decision.status === "proposed" ||
    decision.status === "approved" ||
    decision.status === "rejected"
  );
}

export function csFormatDateTime(value: string | null | undefined): string {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export function csTemplatesKey(brandId: string | undefined): string {
  return `/api/creative-strategist/brands/${brandId}/templates`;
}

export interface CreativeStrategistTemplateBoard {
  brandId: string;
  brandName: string;
  assetTypes: CreativeStrategistCoverageRow[];
  entries: CreativeStrategistTemplateEntry[];
  summary: { readyCount: number; assetTypeCount: number; text: string };
}

export interface CreativeStrategistCoverageRow {
  assetType: string;
  label: string;
  description: string;
  system: string;
  discoverable: boolean;
  defaultTemplateId: string | null;
  defaultName: string | null;
  approvedCount: number;
  testingCount: number;
  unreviewedCount: number;
  retiredCount: number;
  candidateCount: number;
  state: "ready" | "needs_default" | "needs_approval";
}

export interface CreativeStrategistBlocker {
  id: string;
  projectId: string;
  brandId: string;
  taskId: string | null;
  blockerType: string;
  assetType: string | null;
  title: string;
  detail: string | null;
  optionsJson: Array<{ key: string; label: string; description: string }> | null;
  status: string;
  resolutionOption: string | null;
  resolutionNote: string | null;
  resolvedBy: string | null;
  resolvedAt: string | null;
  createdAt: string | null;
}

export function csBlockersKey(projectId: string | undefined): string {
  return `/api/creative-strategist/projects/${projectId}/blockers`;
}

// ── Deliverables, review and Results (Task #2382, spec §52 + §55–§60) ──────

export interface CreativeStrategistDeliverableCard {
  id: string;
  deliverableType: string;
  category: string;
  title: string | null;
  status: string;
  version: number;
  versionCount: number;
  reviewVerdict: string;
  previewUrl: string | null;
  /** Proxied WebP thumb — grids must never load full-size originals. */
  thumbnailUrl: string | null;
  documentContent: string | null;
  approvedBy: string | null;
  approvedAt: string | null;
  createdAt: string | null;
  taskId: string | null;
  taskTitle: string | null;
  attentionReason: string | null;
  output: {
    outputSystem: string;
    outputType: string;
    outputId: string;
    outputUrl: string | null;
  } | null;
}

export interface CreativeStrategistReviewIssue {
  severity?: string;
  area?: string;
  detail?: string;
}

export interface CreativeStrategistDeliverableVersion {
  id: string;
  deliverableId: string;
  versionNumber: number;
  title: string | null;
  previewUrl: string | null;
  thumbnailUrl: string | null;
  documentContent: string | null;
  reviewVerdict: string;
  reviewScore: number | null;
  reviewSummary: string | null;
  issues: CreativeStrategistReviewIssue[];
  criteriaChecks: Array<{ criterion: string; met: boolean; note?: string }>;
  revisionInstructions: string[];
  reviewedAt: string | null;
  createdAt: string | null;
  taskId: string | null;
}

export interface CreativeStrategistResults {
  schemaVersion: 1;
  generatedAt: string;
  projectTitle: string;
  originalRequest: string | null;
  completedCategories: Array<{
    category: string;
    label: string;
    count: number;
    breakdown: Array<{ deliverableType: string; label: string; count: number }>;
  }>;
  deliverableCount: number;
  initiatives: Array<{
    key: string;
    title: string;
    items: Array<{
      deliverableId: string;
      title: string;
      deliverableType: string;
      typeLabel: string;
      category: string;
      status: string;
      version: number;
      previewUrl: string | null;
      outputUrl: string | null;
      reviewVerdict: string;
    }>;
  }>;
  decisions: Array<{
    id: string;
    decisionType: string;
    title: string;
    rationale: string | null;
    source: string;
    status: string;
  }>;
  outstanding: Array<{ taskId: string; title: string; reason: string }>;
  reviewSummary: {
    passedFirstTime: number;
    revised: number;
    escalated: number;
    userApproved: number;
  };
  narrative: {
    headline: string;
    goal: string;
    whatWeBuilt: string;
    majorDecisions: string[];
    keyLearnings: string[];
    outstandingItems: string[];
    recommendedNextWork: { title: string; objective: string; rationale: string } | null;
    generated: boolean;
  };
}

export interface CreativeStrategistResultsState {
  status: string;
  results: CreativeStrategistResults | null;
  generatedAt: string | null;
  projectStatus: string;
  completion: {
    complete: boolean;
    reason: string | null;
    activeTasks: number;
    awaitingUser: number;
    skipped: number;
    failed: number;
    completed: number;
  };
}

export function csDeliverablesKey(projectId: string | undefined): string {
  return `/api/creative-strategist/projects/${projectId}/deliverables`;
}

export function csDeliverableVersionsKey(deliverableId: string | undefined): string {
  return `/api/creative-strategist/deliverables/${deliverableId}/versions`;
}

export function csResultsKey(projectId: string | undefined): string {
  return `/api/creative-strategist/projects/${projectId}/results`;
}
